<div class="page-title">
  <span>
    <span [translate]="'CHEMICALS'"></span>
    <i class="pi pi-angle-right sds-breadcrumb-sep" aria-hidden="true"></i>
    <span [translate]="'SDS_NEW_RECORD'"></span>
  </span>
  <span class="page-actions">
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="pi pi-arrow-left"></i>
      <span [translate]="'BACK_LIST'"></span>
    </button>
  </span>
</div>

<!-- ═══════════ STEP INDICATOR ═══════════ -->
<div class="sds-stepper">
  <div
    *ngFor="let step of steps; let i = index"
    class="sds-step"
    [class.active]="i === currentStep"
    [class.completed]="i < currentStep"
    (click)="goToStep(i)"
  >
    <div class="sds-step-number">
      <span *ngIf="i >= currentStep">{{ i + 1 }}</span>
      <i *ngIf="i < currentStep" class="pi pi-check"></i>
    </div>
    <span class="sds-step-label">{{ step.label }}</span>
  </div>
  <div class="sds-step-line">
    <div class="sds-step-line-fill" [style.width.%]="stepProgressPercent"></div>
  </div>
</div>

<!-- Persistent attached-SDS summary — visible on every step after Step 1.
     (Step 1 shows the file through its own upload control.) -->
<div
  class="sds-pdf-banner"
  *ngIf="currentStep > 0"
  [class.sds-pdf-banner--empty]="!hasPdfFile"
>
  <ng-container *ngIf="hasPdfFile; else noPdfAttached">
    <div
      class="sds-pdf-banner-info sds-pdf-clickable"
      (click)="openPdfPreview()"
      [pTooltip]="'SDS_PREVIEW_PDF_TOOLTIP' | translate"
      tooltipPosition="top"
    >
      <i class="pi pi-file-pdf sds-pdf-banner-icon"></i>
      <span class="sds-pdf-banner-name">{{ uploadedFileName }}</span>
    </div>
    <button
      class="btn btn-outline-primary sds-pdf-preview-btn"
      type="button"
      (click)="openPdfPreview()"
      [pTooltip]="'SDS_PREVIEW_PDF_TOOLTIP' | translate"
      tooltipPosition="top"
    >
      <i class="pi pi-eye"></i>
      <span [translate]="'SDS_PREVIEW_PDF'"></span>
    </button>
  </ng-container>
  <ng-template #noPdfAttached>
    <div class="sds-pdf-banner-info sds-pdf-banner-empty-text">
      <i class="pi pi-info-circle"></i>
      <span [translate]="'SDS_NO_FILE_ATTACHED'"></span>
    </div>
  </ng-template>
</div>

<!-- ═══════════════════════════════════════════════════════════
     STEP 1 — Product Information
     ═══════════════════════════════════════════════════════════ -->
<div class="sds-form-step" *ngIf="currentStep === 0">
  <!-- SECTION 1: Upload or Search SDS (full width) -->
  <div class="sub-panel">
    <div class="sub-panel-title">
      <span *ngIf="showSdsSearch" [translate]="'SDS_UPLOAD_OR_SEARCH'"></span>
      <span *ngIf="!showSdsSearch" [translate]="'SDS_UPLOAD_TITLE'"></span>
    </div>
    <div class="sub-panel-content">
      <!-- AI Search bar (parked for now — toggled via showSdsSearch) -->
      <div class="sds-ai-search-wrapper" *ngIf="showSdsSearch">
        <div class="sds-ai-search-bar">
          <p-iconfield class="sds-search-field">
            <p-inputicon styleClass="pi pi-search" />
            <input
              pInputText
              type="text"
              [(ngModel)]="sdsSearchQuery"
              [placeholder]="'SDS_SEARCH_PLACEHOLDER' | translate"
              class="sds-ai-search-input"
              (keyup.enter)="searchSds()"
              (input)="searchSds()"
            />
          </p-iconfield>
          <button class="btn btn-primary sds-search-btn" (click)="searchSds()">
            <span [translate]="'SDS_SEARCH'"></span>
          </button>
        </div>

        <!-- Search Results -->
        <div class="sds-search-results" *ngIf="sdsSearchResults.length > 0">
          <div
            class="sds-search-result-item"
            *ngFor="let result of sdsSearchResults"
            (click)="selectSdsResult(result)"
          >
            <div class="sds-result-main">
              <i class="pi pi-file-pdf sds-result-icon"></i>
              <div class="sds-result-info">
                <span class="sds-result-name">{{ result.productName }}</span>
                <span class="sds-result-meta">
                  {{ result.supplier }} · CAS {{ result.casNumber }}
                </span>
              </div>
            </div>
            <div class="sds-result-score">
              <span class="sds-match-badge">{{ result.matchScore }}%</span>
            </div>
          </div>
        </div>
        <div class="sds-searching" *ngIf="isSearching || isImportingSearchResult">
          <i class="pi pi-spin pi-spinner"></i> <span [translate]="'SDS_SEARCHING'"></span>
        </div>
      </div>

      <!-- File Upload -->
      <div class="sds-upload-section" [class.sds-upload-section--solo]="!showSdsSearch">
        <label class="col-form-label" *ngIf="showSdsSearch">
          <span [translate]="'SDS_OR_UPLOAD_DIRECTLY'"></span>
        </label>
        <div class="sds-upload-controls">
          <p-fileUpload
            #sdsFileUpload
            mode="basic"
            name="sdsFile"
            accept=".pdf,.doc,.docx"
            [maxFileSize]="10000000"
            chooseLabel="Choose File"
            (onSelect)="onFileSelect($event)"
            [auto]="false"
          ></p-fileUpload>
          <button
            class="btn btn-outline-primary"
            type="button"
            (click)="openArchivePicker()"
          >
            <i class="pi pi-folder-open"></i>
            <span [translate]="'SDS_ADD_FROM_ARCHIVE'"></span>
          </button>
        </div>
        <div *ngIf="uploadedFileName" class="sds-uploaded-file">
          <span
            class="sds-uploaded-file-open"
            (click)="openPdfPreview()"
            [pTooltip]="'SDS_PREVIEW_PDF_TOOLTIP' | translate"
            tooltipPosition="top"
          >
            <i class="pi pi-file-pdf"></i>
            <span class="sds-uploaded-file-name">{{ uploadedFileName }}</span>
          </span>
          <button
            *ngIf="hasPdfFile"
            class="btn btn-outline-primary sds-pdf-preview-btn"
            type="button"
            (click)="openPdfPreview()"
            [pTooltip]="'SDS_PREVIEW_PDF_TOOLTIP' | translate"
            tooltipPosition="top"
          >
            <i class="pi pi-eye"></i>
            <span [translate]="'SDS_PREVIEW_PDF'"></span>
          </button>
          <button
            class="btn btn-icon"
            type="button"
            (click)="onFileClear()"
            title="Remove"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>
    </div>

    <app-sds-archive-picker
      [businessId]="businessId"
      [(visible)]="archivePickerVisible"
      (fileSelected)="onArchiveFileSelected($event)"
    ></app-sds-archive-picker>
  </div>

  <!-- SECTION 2: Product Details (full width, below upload) -->
  <div class="sub-panel">
    <div class="sub-panel-title">
      <span [translate]="'SDS_PRODUCT_DETAILS'"></span>
    </div>
    <div class="sub-panel-content">
      <form [formGroup]="step1Form">
        <div class="row">
          <!-- Product Name -->
          <div class="col-sm-6">
            <div class="form-group">
              <label for="productName" class="col-form-label">
                <span [translate]="'SDS_PRODUCT_NAME'"></span>
                <span class="required">*</span>
              </label>
              <div>
                <input
                  type="text"
                  pInputText
                  id="productName"
                  formControlName="ProductName"
                  class="ex-full-width"
                  [placeholder]="'SDS_PRODUCT_NAME_PLACEHOLDER' | translate"
                  maxlength="255"
                />
              </div>
              <control-messages [control]="step1Form.controls['ProductName']"></control-messages>
            </div>
          </div>

          <!-- SDS Manufacturer (Dropdown + Add) -->
          <div class="col-sm-6">
            <div class="form-group">
              <label for="supplier" class="col-form-label">
                <span [translate]="'SDS_SUPPLIER'"></span>
                <span class="required">*</span>
              </label>
              <div class="input-btn-group">
                <p-select
                  [options]="suppliers"
                  formControlName="Supplier"
                  [style]="{ width: '100%' }"
                  [filter]="true"
                  [filterPlaceholder]="'SDS_SEARCH_MANUFACTURERS_PLACEHOLDER' | translate"
                  panelStyleClass="sds-manufacturer-panel"
                >
                  <ng-template let-option pTemplate="item">
                    <div class="sds-manufacturer-row">
                      <span class="sds-manufacturer-name">{{ option.label }}</span>
                      <span *ngIf="option.isDefault" class="sds-manufacturer-badge" [translate]="'SDS_MANUFACTURER_DEFAULT'"></span>
                    </div>
                  </ng-template>
                </p-select>
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  (click)="openAddSupplier()"
                  [pTooltip]="'SDS_ADD_MANUFACTURER_TOOLTIP' | translate"
                  tooltipPosition="top"
                >
                  <i class="pi pi-plus" aria-hidden="true"></i>
                </button>
              </div>
              <control-messages [control]="step1Form.controls['Supplier']"></control-messages>
            </div>
          </div>

          <!-- CAS Number(s) — a document may list several -->
          <div class="col-sm-6">
            <div class="form-group">
              <label for="casNumber" class="col-form-label">
                <span [translate]="'SDS_CAS_NUMBER'"></span>
                <span *ngIf="!step1Form.controls['NoCasNumber'].value" class="text-danger">*</span>
              </label>
              <div>
                <p-autoComplete [multiple]="true" [typeahead]="false"
                  inputId="casNumber"
                  formControlName="CASNumbers"
                  separator=","
                  [unique]="true"
                  [addOnTab]="true"
                  [addOnBlur]="true"
                  styleClass="ex-full-width sds-cas-chips"
                  placeholder="e.g. 67-64-1, 7732-18-5"
                ></p-autoComplete>
              </div>

              <label class="sds-no-cas-option">
                <input type="checkbox" formControlName="NoCasNumber" />
                <span [translate]="'SDS_NO_CAS_NUMBER'"></span>
              </label>

              <!-- PDF CAS auto-detection feedback -->
              <small *ngIf="scanningCasFromPdf" class="sds-cas-scan-status">
                <i class="pi pi-spin pi-spinner"></i>
                <span [translate]="'SDS_CAS_SCANNING'"></span>
              </small>

              <control-messages [control]="step1Form.controls['CASNumbers']"></control-messages>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- SECTION 3: Pictogram Selection (full width) -->
  <div class="sub-panel">
    <div class="sub-panel-title">
      <span [translate]="'SDS_GHS_PICTOGRAMS'"></span>
      <span class="sds-pictogram-counter" *ngIf="selectedPictograms.length">({{ selectedPictograms.length }})</span>
    </div>
    <div class="sub-panel-content">
      <p class="sds-pictogram-hint" [translate]="'SDS_PICTOGRAM_HINT_NO_LIMIT'"></p>
      <div class="sds-pictogram-grid">
        <div
          *ngFor="let p of pictogramList"
          class="sds-pictogram-card"
          [class.selected]="isPictogramSelected(p.id)"
          (click)="togglePictogram(p.id)"
          [pTooltip]="p.ghs + ' · ' + (('SDS_PICTOGRAM_' + p.ghs) | translate)"
          tooltipPosition="top"
        >
          <img [src]="p.svg" [alt]="('SDS_PICTOGRAM_' + p.ghs) | translate" class="sds-pictogram-card-img" />
          <span class="sds-pictogram-card-label">{{ ('SDS_PICTOGRAM_' + p.ghs) | translate }}</span>
          <div class="sds-pictogram-check" *ngIf="isPictogramSelected(p.id)">
            <i class="pi pi-check-circle"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════
     STEP 2 — Main Hazard & Use of Product
     ═══════════════════════════════════════════════════════════ -->
<div class="sds-form-step" *ngIf="currentStep === 1">

  <form [formGroup]="step2Form">
    <div class="row">
      <!-- LEFT: Hazard Info -->
      <div class="col-sm-6">
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_HAZARD_INFORMATION'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'SDS_H_STATEMENT_CODE'"></span>
              </label>
              <div>
                <sds-hazard-picker [control]="step2Form.controls['HazardStatements']"></sds-hazard-picker>
              </div>
            </div>

            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'DESCRIPTION'"></span>
              </label>
              <div>
                <textarea
                  pTextarea
                  formControlName="Description"
                  class="ex-description"
                  rows="4"
                  [placeholder]="'SDS_DESCRIPTION_PLACEHOLDER' | translate"
                  maxlength="2000"
                ></textarea>
              </div>
            </div>

            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'SDS_KEY_RISK'"></span>
              </label>
              <div>
                <textarea
                  pTextarea
                  formControlName="KeyRisk"
                  class="ex-description"
                  rows="3"
                  [placeholder]="'SDS_KEY_RISK_PLACEHOLDER' | translate"
                  maxlength="2000"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Use of Product -->
      <div class="col-sm-6">
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_USE_OF_PRODUCT'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'SDS_AREA_OF_USE'"></span>
              </label>
              <div>
                <textarea
                  pTextarea
                  formControlName="TaskActivity"
                  class="ex-description"
                  rows="3"
                  [placeholder]="'SDS_AREA_OF_USE' | translate"
                  maxlength="2000"
                ></textarea>
              </div>
            </div>

            <div class="form-group sds-exposure-tracking-toggle">
              <p-checkbox
                [binary]="true"
                formControlName="RequiresExposureTracking"
                inputId="requiresExposureTracking"
              ></p-checkbox>
              <label for="requiresExposureTracking" class="col-form-label">
                <span [translate]="'SDS_REQUIRES_EXPOSURE_TRACKING'"></span>
              </label>
            </div>

            <div class="form-group" *ngIf="requiresExposureTracking">
              <label class="col-form-label">
                <span [translate]="'SDS_HAZARD_CATEGORY'"></span>
              </label>
              <div>
                <p-select
                  formControlName="HazardCategory"
                  [options]="hazardCategoryOptions"
                  appendTo="body">
                </p-select>
                <small class="form-hint" [translate]="'SDS_HAZARD_CATEGORY_HINT'"></small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>

<!-- ═══════════════════════════════════════════════════════════
     STEP 3 — Existing Controls & Risk Assessment
     ═══════════════════════════════════════════════════════════ -->
<div class="sds-form-step" *ngIf="requiresExposureTracking && currentStep === 2">
  <form [formGroup]="exposureForm">
    <div class="sub-panel">
      <div class="sub-panel-title">
        <span [translate]="'SDS_STEP_EXPOSURE_LOG'"></span>
        <button
          *ngIf="canAccessEmployeeExposureData"
          class="btn btn-outline-primary btn-sm"
          type="button"
          (click)="addEmployeeExposure()">
          <i class="pi pi-plus"></i>
          <span [translate]="'SDS_ADD_EXPOSURE_EMPLOYEE'"></span>
        </button>
      </div>
      <div class="sub-panel-content">
        <div class="sds-exposure-warning">
          <i class="pi pi-info-circle"></i>
          <span [translate]="'SDS_EXPOSURE_LOG_WARNING'"></span>
        </div>

        <div *ngIf="!canAccessEmployeeExposureData" class="sds-exposure-warning">
          <i class="pi pi-lock"></i>
          <span [translate]="'SDS_EXPOSURE_LOCKED_EDITOR'"></span>
        </div>

        <div
          *ngIf="canAccessEmployeeExposureData"
          formArrayName="EmployeeExposures"
          class="sds-exposure-grid">
          <div class="sds-exposure-required-legend">
            <span class="sds-required-marker" aria-hidden="true">*</span>
            <span [translate]="'SDS_REQUIRED_FIELD_LEGEND'"></span>
          </div>

          <div
            class="sds-exposure-card"
            *ngFor="let exposure of employeeExposures.controls; let i = index"
            [formGroupName]="i"
          >
            <!-- Card header: row index + delete action pinned to the right -->
            <div class="sds-exposure-card-header">
              <span class="sds-exposure-card-title">
                <i class="pi pi-user"></i>
                <span>{{ 'SDS_EMPLOYEE' | translate }} #{{ i + 1 }}</span>
              </span>
              <button
                class="sds-exposure-card-remove"
                type="button"
                (click)="removeEmployeeExposure(i)"
                [disabled]="employeeExposures.length === 1"
                [pTooltip]="'DELETE' | translate"
                tooltipPosition="left"
                aria-label="Remove exposure">
                <i class="pi pi-trash"></i>
              </button>
            </div>

            <!-- Identity group -->
            <div class="sds-exposure-section-label" [translate]="'SDS_EMPLOYEE'"></div>

            <!-- Pick an existing employee to auto-fill name/email (ID number stays manual). -->
            <div class="sds-field sds-employee-picker" *ngIf="employeeOptions.length">
              <label class="sds-field-label">
                <span [translate]="'SDS_EMPLOYEE_PICKER_LABEL'"></span>
                <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
              </label>
              <p-select
                [options]="employeeOptions"
                formControlName="EmployeeUserId"
                [filter]="true"
                [showClear]="true"
                filterBy="label"
                appendTo="body"
                styleClass="sds-employee-picker-dropdown"
                [placeholder]="'SDS_EMPLOYEE_PICKER_PLACEHOLDER' | translate"
                (onChange)="onEmployeeSelected(i, $event.value)">
              </p-select>
              <small class="sds-field-hint" [translate]="'SDS_EMPLOYEE_PICKER_HINT'"></small>
            </div>

            <div class="sds-exposure-fieldset">
              <div class="sds-field">
                <label class="sds-field-label">
                  <span [translate]="'SDS_EMPLOYEE_FIRST_NAME'"></span>
                  <span class="sds-required-marker" aria-hidden="true">*</span>
                </label>
                <input type="text" pInputText formControlName="FirstName" />
              </div>
              <div class="sds-field">
                <label class="sds-field-label">
                  <span [translate]="'SDS_EMPLOYEE_LAST_NAME'"></span>
                  <span class="sds-required-marker" aria-hidden="true">*</span>
                </label>
                <input type="text" pInputText formControlName="LastName" />
              </div>
              <div class="sds-field">
                <label class="sds-field-label">
                  <span>Email</span>
                  <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                </label>
                <input type="email" pInputText formControlName="Email" />
              </div>
              <div class="sds-field">
                <label class="sds-field-label">
                  <span [translate]="'ADDRESS'"></span>
                  <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                </label>
                <input type="text" pInputText formControlName="Address" />
              </div>
            </div>

            <!-- Compliance group: NIN + (asbestos) medical exam fields -->
            <ng-container *ngIf="requiresNationalIdentityNumber || isAsbestos">
              <div class="sds-exposure-section-label" [translate]="'SDS_HAZARD_CATEGORY'"></div>
              <div class="sds-exposure-fieldset sds-exposure-compliance-fieldset">
                <div class="sds-field" *ngIf="requiresNationalIdentityNumber">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_NIN_PLACEHOLDER'"></span>
                    <span class="sds-required-marker" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="text"
                    pInputText
                    formControlName="NationalIdentityNumber"
                    autocomplete="off"
                    maxlength="11"
                    [pTooltip]="'SDS_NIN_TOOLTIP_STORED_ENCRYPTED' | translate"
                    tooltipPosition="top"
                  />
                  <small
                    *ngIf="exposure.get('NationalIdentityNumber')?.touched && exposure.get('NationalIdentityNumber')?.errors as ninErrors"
                    class="sds-field-error">
                    <span *ngIf="ninErrors['ninFormat']" [translate]="'SDS_NIN_VALIDATION_FORMAT'"></span>
                  </small>
                </div>
                <div class="sds-field" *ngIf="isAsbestos">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_MEDICAL_EXAM_DATE'"></span>
                    <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                  </label>
                  <p-datepicker
                    formControlName="MedicalExamDate"
                    dateFormat="dd/mm/yy"
                    [showIcon]="true"
                    appendTo="body"
                    styleClass="sds-calendar-clean"
                  ></p-datepicker>
                </div>
                <div class="sds-field" *ngIf="isAsbestos">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_MEDICAL_EXAM_DOCTOR_NAME'"></span>
                    <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                  </label>
                  <input type="text" pInputText formControlName="MedicalExamDoctorName" />
                </div>
              </div>
            </ng-container>

            <!-- Exposure dates -->
            <div class="sds-exposure-section-label" [translate]="'SDS_EXPOSURE_START_DATE'"></div>
            <div class="sds-exposure-fieldset sds-exposure-date-fieldset">
              <div class="sds-field">
                <label class="sds-field-label">
                  <span [translate]="'SDS_EXPOSURE_START_DATE'"></span>
                  <span class="sds-required-marker" aria-hidden="true">*</span>
                </label>
                <p-datepicker
                  formControlName="ExposureStartDate"
                  dateFormat="dd/mm/yy"
                  [showIcon]="true"
                  appendTo="body"
                  styleClass="sds-calendar-clean"
                ></p-datepicker>
              </div>
              <div class="sds-field">
                <label class="sds-field-label">
                  <span [translate]="'SDS_EXPOSURE_END_DATE'"></span>
                  <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                </label>
                <p-datepicker
                  formControlName="ExposureEndDate"
                  dateFormat="dd/mm/yy"
                  [showIcon]="true"
                  appendTo="body"
                  styleClass="sds-calendar-clean"
                  [pTooltip]="'SDS_EXPOSURE_END_DATE_HINT' | translate"
                  tooltipPosition="top"
                ></p-datepicker>
              </div>
            </div>
          </div>
        </div>

        <div
          *ngIf="canAccessEmployeeExposureData && !isExposureStepValid()"
          class="sds-validation-msg" [translate]="'SDS_EXPOSURE_LOG_REQUIRED'"></div>
      </div>
    </div>
  </form>
</div>

<div class="sds-form-step" *ngIf="currentStep === finalStepIndex">
  <form [formGroup]="step3Form">
    <div class="row">
      <!-- LEFT: Existing Controls -->
      <div class="col-sm-12">
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_EXISTING_CONTROLS'"></span>
          </div>
          <div class="sub-panel-content">
            <!-- Other Controls -->
            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'SDS_OTHER_CONTROLS'"></span>
              </label>
              <div>
                <textarea
                  pTextarea
                  formControlName="OtherControls"
                  class="ex-description"
                  rows="3"
                  [placeholder]="'SDS_OTHER_CONTROLS_PLACEHOLDER' | translate"
                  maxlength="2000"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Risk Level Assessment (full width, below) -->
    <div class="sub-panel">
      <div class="sub-panel-title">
        <span [translate]="'SDS_RISK_LEVEL'"></span>
      </div>
      <div class="sub-panel-content">
        <div class="row">
          <!-- Probability -->
          <div class="col-sm-4">
            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'PROBABILITY_FIGURE'"></span>
              </label>
              <div>
                <p-select
                  [options]="figureValue"
                  formControlName="Probability"
                  (onChange)="probabilityChanged($event)"
                  [style]="{ width: '100%' }"
                ></p-select>
              </div>
            </div>
          </div>

          <!-- Consequence -->
          <div class="col-sm-4">
            <div class="form-group">
              <label class="col-form-label">
                <span [translate]="'CONSEQUENCE_FIGURE'"></span>
              </label>
              <div>
                <p-select
                  [options]="figureValue"
                  formControlName="Consequence"
                  (onChange)="consequenceChanged($event)"
                  [style]="{ width: '100%' }"
                ></p-select>
              </div>
            </div>
          </div>

          <!-- Risk Figure (calculated) -->
          <div class="col-sm-4">
            <div class="form-group" *ngIf="riskData">
              <label class="col-form-label">
                <span [translate]="'RISK_FIGURE'"></span>
              </label>
              <div>
                <input
                  type="text"
                  readonly
                  [style.background-color]="riskData.backColor"
                  pInputText
                  class="ex-full-width sds-risk-input"
                  [value]="riskData.value | translate"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Save sits above the (tall) reference chart so it's reachable without scrolling -->
    <div class="sds-step3-save">
      <button
        class="btn btn-success"
        (click)="saveRecord()"
        [disabled]="!isStep3Valid()"
      >
        <span [translate]="'SAVE'"></span>
      </button>
    </div>

    <!-- Risk Matrix Reference (full width, below risk level) -->
    <div class="sub-panel">
      <div class="sub-panel-title">
        <span [translate]="'SDS_RISK_MATRIX_REFERENCE'"></span>
      </div>
      <div class="sub-panel-content sds-risk-matrix-wrapper">
        <staticdata></staticdata>
      </div>
    </div>

  </form>
</div>

<!-- ═══════════ NAVIGATION BUTTONS ═══════════ -->
<div class="action-btns-wrapper sds-action-bar">
  <button class="btn btn-outline-secondary" (click)="goBack()">
    <i class="pi pi-arrow-left"></i>
    <span [translate]="'BACK_LIST'"></span>
  </button>

  <div class="sds-action-right">
    <button
      *ngIf="currentStep > 0"
      class="btn btn-outline-primary"
      (click)="prevStep()"
    >
      <i class="pi pi-arrow-left"></i>
      <span [translate]="'SDS_PREVIOUS'"></span>
    </button>

    <button
      *ngIf="currentStep < finalStepIndex"
      class="btn btn-primary"
      (click)="nextStep()"
      [disabled]="currentStep === 0 && !isStep1Valid() || currentStep === 1 && !isStep2Valid() || requiresExposureTracking && currentStep === 2 && !isExposureStepValid()"
    >
      <span [translate]="'SDS_NEXT'"></span>
      <i class="pi pi-arrow-right"></i>
    </button>

  </div>
</div>

<!-- ═══════════ ADD MANUFACTURER DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_NEW_SUPPLIER' | translate"
  [(visible)]="addSupplierVisible"
  [style]="{ width: '400px' }"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
>
  <div class="form-group">
    <label class="col-form-label">
      <span [translate]="'SDS_SUPPLIER_NAME'"></span>
    </label>
    <div>
      <input
        type="text"
        pInputText
        [(ngModel)]="newSupplierName"
        class="ex-full-width"
        [placeholder]="'SDS_SUPPLIER_NAME_PLACEHOLDER' | translate"
        (keyup.enter)="saveNewSupplier()"
      />
    </div>
  </div>
  <ng-template pTemplate="footer">
    <button
      class="btn btn-primary"
      [disabled]="!newSupplierName.trim()"
      (click)="saveNewSupplier()"
    >
      <span [translate]="'SAVE'"></span>
    </button>
  </ng-template>
</p-dialog>

<!-- ═══════════ PDF PREVIEW DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_PDF_PREVIEW_TITLE' | translate"
  [(visible)]="pdfPreviewVisible"
  [style]="{ width: '90vw', height: '90vh' }"
  [baseZIndex]="10000"
  [maximizable]="true"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
  (onHide)="closePdfPreview()"
  styleClass="sds-pdf-dialog"
>
  <div class="sds-pdf-dialog-toolbar">
    <div class="sds-pdf-dialog-hint">
      <i class="pi pi-info-circle"></i>
      <span [translate]="'SDS_PDF_PREVIEW_HINT'"></span>
    </div>
    <button
      *ngIf="uploadedFile"
      class="btn btn-outline-primary sds-pdf-download-btn"
      type="button"
      (click)="downloadPdf()"
      [pTooltip]="'SDS_DOWNLOAD_PDF' | translate"
      tooltipPosition="left"
    >
      <i class="pi pi-download"></i>
      <span [translate]="'SDS_DOWNLOAD_PDF'"></span>
    </button>
  </div>
  <div class="sds-pdf-loading" *ngIf="pdfPreviewLoading">
    <i class="pi pi-spin pi-spinner"></i>
    <span [translate]="'SDS_PDF_LOADING'"></span>
  </div>
  <div class="sds-pdf-iframe-wrapper" *ngIf="!pdfPreviewLoading && pdfPreviewUrl">
    <iframe
      [src]="pdfPreviewUrl"
      class="sds-pdf-iframe"
      frameborder="0"
    ></iframe>
  </div>
  <div class="sds-pdf-no-file" *ngIf="!pdfPreviewLoading && !pdfPreviewUrl">
    <i class="pi pi-exclamation-triangle"></i>
    <span [translate]="'SDS_PDF_NOT_AVAILABLE'"></span>
  </div>
</p-dialog>

<!-- ═══════════ TOASTER & LOADER ═══════════ -->

<p-progressBar
  mode="indeterminate"
  [style]="{ height: '6px' }"
  *ngIf="loading"
></p-progressBar>

<kuba-busy [busy]="loading" message="PROCESSING"></kuba-busy>

